import { getServerRuntimeConfig } from '@lib/runtime-config';
import { getLogger } from '@lib/util/logging-util';

export default async function medusaRequest(
    method: string,
    path = '',
    payload?: {
        query?: Record<string, any>;
        body?: Record<string, any>;
    }
) {
    const requestId = `medusa-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    const logger = getLogger();

    // Get runtime config for server-side
    const config = getServerRuntimeConfig();
    const MEDUSA_API_KEY = config.MEDUSA_API_KEY || process.env.MEDUSA_API_KEY || '';
    const REVALIDATE_WINDOW = parseInt((config.REVALIDATE_WINDOW || process.env.REVALIDATE_WINDOW || 60 * 30).toString());
    const ENDPOINT = config.MEDUSA_BACKEND_URL || process.env.MEDUSA_BACKEND_URL || '';

    logger.verbose(`[medusaRequest][${requestId}] ========== START ==========`);
    logger.verbose(`[medusaRequest][${requestId}] Timestamp: ${new Date().toISOString()}`);
    logger.verbose(`[medusaRequest][${requestId}] Method: ${method}`);
    logger.verbose(`[medusaRequest][${requestId}] Path (original): ${path}`);
    logger.verbose(`[medusaRequest][${requestId}] Payload:`, JSON.stringify(payload, null, 2));
    logger.verbose(`[medusaRequest][${requestId}] Runtime Config:`);
    logger.verbose(`[medusaRequest][${requestId}]   MEDUSA_BACKEND_URL from config: ${config.MEDUSA_BACKEND_URL || '(NOT SET)'}`);
    logger.verbose(`[medusaRequest][${requestId}]   MEDUSA_BACKEND_URL from env: ${process.env.MEDUSA_BACKEND_URL || '(NOT SET)'}`);
    logger.verbose(`[medusaRequest][${requestId}]   Final ENDPOINT: ${ENDPOINT}`);
    logger.verbose(`[medusaRequest][${requestId}]   MEDUSA_API_KEY: ${MEDUSA_API_KEY ? '***SET*** (length: ' + MEDUSA_API_KEY.length + ')' : '(NOT SET)'}`);
    logger.verbose(`[medusaRequest][${requestId}]   REVALIDATE_WINDOW: ${REVALIDATE_WINDOW}`);

    // Validate ENDPOINT is an absolute URL
    if (!ENDPOINT) {
        logger.error(`[medusaRequest][${requestId}] ========== CONFIGURATION ERROR ==========`);
        logger.error(`[medusaRequest][${requestId}] MEDUSA_BACKEND_URL is not set!`);
        logger.error(`[medusaRequest][${requestId}] This will cause requests to be resolved relative to the current page.`);
        throw new Error('MEDUSA_BACKEND_URL is not configured');
    }

    if (!ENDPOINT.startsWith('http://') && !ENDPOINT.startsWith('https://')) {
        logger.error(`[medusaRequest][${requestId}] ========== CONFIGURATION ERROR ==========`);
        logger.error(`[medusaRequest][${requestId}] MEDUSA_BACKEND_URL is not an absolute URL: ${ENDPOINT}`);
        logger.error(`[medusaRequest][${requestId}] It must start with http:// or https://`);
        logger.error(`[medusaRequest][${requestId}] Current value will be resolved relative to the current page!`);
    }

    const options: RequestInit = {
        method,
        headers: {
            'Content-Type': 'application/json',
            'x-publishable-key': MEDUSA_API_KEY,
            Accept: 'application/json',
        },
        next: {
            revalidate: REVALIDATE_WINDOW,
            tags: ['medusa_request'],
        },
    };

    if (payload?.body) {
        options.body = JSON.stringify(payload.body);
    }

    if (payload?.query) {
        const params = objectToURLSearchParams(payload.query).toString();
        path = `${path}?${params}`;
    }

    logger.verbose(`[medusaRequest][${requestId}] Path (after query params): ${path}`);

    const limit = payload?.query?.limit || 100;
    const offset = payload?.query?.offset || 0;

    try {
        const requestPath = `${ENDPOINT}${path}`;

        logger.verbose(`[medusaRequest][${requestId}] ========== REQUEST ==========`);
        logger.verbose(`[medusaRequest][${requestId}] Full URL: ${requestPath}`);
        logger.verbose(`[medusaRequest][${requestId}] Headers:`, JSON.stringify(options.headers, null, 2));
        if (payload?.body) {
            logger.verbose(`[medusaRequest][${requestId}] Body:`, JSON.stringify(payload.body, null, 2));
        }

        const startTime = Date.now();

        logger.verbose(`[medusaRequest][${requestId}] Sending request at ${new Date(startTime).toISOString()}...`);

        const result = await fetch(requestPath, options);

        const duration = Date.now() - startTime;

        logger.verbose(`[medusaRequest][${requestId}] Response received in ${duration}ms`);
        logger.verbose(`[medusaRequest][${requestId}] ========== RESPONSE ==========`);
        logger.verbose(`[medusaRequest][${requestId}] Status: ${result?.status} ${result?.statusText}`);
        logger.verbose(`[medusaRequest][${requestId}] OK: ${result?.ok}`);

        const body = await result.json();

        if (body.errors) {
            logger.error(`[medusaRequest][${requestId}] ========== ERRORS IN RESPONSE ==========`);
            logger.error(`[medusaRequest][${requestId}] Errors:`, JSON.stringify(body.errors, null, 2));
            logger.error(`[medusaRequest][${requestId}] ========== END ==========`);
            throw body.errors[0];
        }

        const nextPage = offset + limit;
        body.nextPage = body.count > nextPage ? nextPage : null;

        logger.verbose(`[medusaRequest][${requestId}] ========== SUCCESS ==========`);
        logger.verbose(`[medusaRequest][${requestId}] Total duration: ${duration}ms`);
        logger.verbose(`[medusaRequest][${requestId}] ========== END ==========`);

        return {
            status: result.status,
            ok: result.ok,
            body,
        };
    } catch (error: any) {
        logger.error(`[medusaRequest][${requestId}] ========== EXCEPTION ==========`);
        logger.error(`[medusaRequest][${requestId}] Error type: ${error?.constructor?.name}`);
        logger.error(`[medusaRequest][${requestId}] Error message: ${error?.message}`);
        logger.error(`[medusaRequest][${requestId}] Error stack:`, error?.stack);
        logger.error(`[medusaRequest][${requestId}] ========== END ==========`);
        throw new Error(error.message || 'An error occurred during the request');
    }
}

function objectToURLSearchParams(obj: Record<string, any>): URLSearchParams {
    const params = new URLSearchParams();

    for (const key in obj) {
        if (Array.isArray(obj[key])) {
            obj[key].forEach((value: any) => {
                params.append(`${key}[]`, value);
            });
        } else {
            params.append(key, obj[key]);
        }
    }

    return params;
}
